import { SessionExpireeError, TraductionIntrouvableError, getApi } from '@/lib/api';
import { preparerRelance } from '@/lib/brouillon';
import { useToast } from '@/lib/notifications';
import { lireSession } from '@/lib/session';
import {
  type EntreeSuivi,
  INTERVALLE_SUIVI_MS,
  abonnerSuivi,
  lireAttendues,
  lireSuivi,
  retirerSuivi,
} from '@/lib/suivi-traductions';
import { cleTraduction } from '@/lib/use-lecture-traduction';
import type { Traduction } from '@rosetta/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useSyncExternalStore } from 'react';

/** The words of the toast T1: the title is optional (a translation can be sent without one). */
function avecTitre(titre: string, avec: (titre: string) => string, sans: string): string {
  return titre === '' ? sans : avec(titre);
}

export const messageTraduite = (titre: string) =>
  avecTitre(titre, (t) => `Votre texte « ${t} » est traduit.`, 'Votre texte est traduit.');

/** What a screen reader announces for T1: it adds where to find the translation again. */
export const annonceTraduite = (titre: string) =>
  avecTitre(
    titre,
    (t) => `Votre texte « ${t} » est traduit, retrouvez-le dans l'historique`,
    "Votre texte est traduit, retrouvez-le dans l'historique",
  );

export const messageEchec = (titre: string) =>
  avecTitre(
    titre,
    (t) => `La traduction « ${t} » n'a pas abouti.`,
    "La traduction n'a pas abouti.",
  );

/** A translation that ended with the translation of some noyaux only (#13). */
export const messagePartielle = (titre: string) =>
  avecTitre(
    titre,
    (t) => `La traduction « ${t} » n'a abouti que pour une partie des noyaux.`,
    "La traduction n'a abouti que pour une partie des noyaux.",
  );

/** A translation one of whose steps failed: it will not end on its own (#13). */
export const messageBloquee = (titre: string) =>
  avecTitre(titre, (t) => `La traduction « ${t} » est bloquée.`, 'La traduction est bloquée.');

interface LecteurSuiviProps {
  entree: EntreeSuivi;
  /** Where the user is: no toast for the translation whose result is open. */
  chemin: string;
}

/**
 * The statuses a translation does not leave on its own: nothing is read after one of them. A
 * blocked one (#13) is among them: it stays "en cours" in Tahiti, but nothing will change it.
 */
const STATUTS_FINAUX = ['terminee', 'partielle', 'echouee', 'interrompue', 'bloquee'];

/**
 * Follows one translation of the list: reads it every 5 s, through the query the waiting
 * screen and the result share, and announces its end once (T1 finished, T2 failed; #13: an
 * error toast with "voir le résultat" for a partial one, and with "voir la traduction" for a
 * blocked one) before it leaves the list. On the page of that very translation (the result or the analysis; the
 * waiting screen and the result also mark it as awaited, which keeps this reader off it)
 * nothing is announced: the page shows the outcome itself (the result, or the failure toast of
 * its redirection). An interrupted translation is never announced: the user asked for it, or it
 * is the interruption screen's own business.
 */
function LecteurSuivi({ entree, chemin }: LecteurSuiviProps) {
  const { id, titre } = entree;
  const toast = useToast();
  const navigate = useNavigate();
  const client = useQueryClient();
  const surSaPage = chemin === `/resultat/${id}` || chemin.startsWith(`/resultat/${id}/`);
  const lecture = useQuery<Traduction, Error>({
    queryKey: cleTraduction(id),
    queryFn: () => {
      const session = lireSession();
      if (session === null) throw new SessionExpireeError();
      return getApi().lireTraduction(session, id);
    },
    staleTime: 0,
    // The 5 s that follow are the retry: a blip of the service does not stop the follow-up.
    retry: false,
    // On its own page the screen reads it already, at the same pace (the same query): reading
    // it twice would only double the calls.
    refetchInterval: (requete) =>
      surSaPage || STATUTS_FINAUX.includes(requete.state.data?.statut ?? '')
        ? false
        : INTERVALLE_SUIVI_MS,
  });

  // The outcome is a reaction to the state of the query, not a fetch: it runs once.
  const annonce = useRef(false);
  useEffect(() => {
    if (annonce.current) return;
    const { data, error } = lecture;

    if (data !== undefined && STATUTS_FINAUX.includes(data.statut)) {
      annonce.current = true;
      retirerSuivi(id);
      // The other translations of the same document (#11, #24: an add ends while the result of the
      // first one is open) read again: the noyau just translated joins their tabs.
      void client.invalidateQueries({
        queryKey: ['traduction'],
        predicate: (requete) => requete.queryKey[1] !== id,
      });
      if (data.statut === 'interrompue' || surSaPage) return;

      const voir = (libelle: string) => ({
        libelle,
        onClick: () => {
          void navigate({ to: '/resultat/$id', params: { id } });
        },
      });
      if (data.statut === 'terminee') {
        toast.afficher({
          variant: 'neutre',
          message: messageTraduite(titre),
          annonce: annonceTraduite(titre),
          action: voir('voir le résultat'),
        });
      } else if (data.statut === 'partielle') {
        // Some translations are ready: the result shows them, and says which did not succeed.
        toast.afficher({
          variant: 'erreur',
          message: messagePartielle(titre),
          action: voir('voir le résultat'),
        });
      } else if (data.statut === 'bloquee') {
        // Nothing will end it: its page says what failed, and offers "interrompre" and "relancer".
        toast.afficher({
          variant: 'erreur',
          message: messageBloquee(titre),
          action: voir('voir la traduction'),
        });
      } else {
        toast.afficher({
          variant: 'erreur',
          message: messageEchec(titre),
          action: {
            libelle: 'relancer',
            onClick: () => {
              preparerRelance({
                id,
                titre,
                texteOrigine: data.texteOrigine,
                noyaux: data.noyaux,
              });
              void navigate({ to: '/' });
            },
          },
        });
      }
    } else if (
      error instanceof SessionExpireeError ||
      error instanceof TraductionIntrouvableError
    ) {
      // No session to read with, or nothing to read: there is nothing left to follow.
      annonce.current = true;
      retirerSuivi(id);
    }
  }, [lecture, surSaPage, id, titre, toast, navigate, client]);

  return null;
}

/**
 * The follow-up of the translations launched from the input screen (T1 and T2, #5). It
 * lives at the root of the app, so that it works whatever the page: it reads every
 * translation of the list (`lib/suivi-traductions.ts`) except the one a screen of that
 * translation waits for (the waiting screen W1 and the result, #7: they show the outcome
 * themselves), and the toast tells the user the outcome. A translation of another account is
 * dropped from the list. It draws nothing itself.
 */
export function FournisseurSuivi() {
  const suivi = useSyncExternalStore(abonnerSuivi, lireSuivi);
  const attendues = useSyncExternalStore(abonnerSuivi, lireAttendues);
  const chemin = useRouterState({ select: (etat) => etat.location.pathname });
  // The session is read at each render; a change of page (login, logout) renders again.
  const utilisateurId = lireSession()?.utilisateur.id;

  useEffect(() => {
    if (utilisateurId === undefined) return;
    for (const entree of suivi) {
      if (entree.utilisateurId !== utilisateurId) retirerSuivi(entree.id);
    }
  }, [suivi, utilisateurId]);

  if (utilisateurId === undefined) return null;
  return (
    <>
      {suivi
        .filter((entree) => entree.utilisateurId === utilisateurId && !attendues.has(entree.id))
        .map((entree) => (
          <LecteurSuivi key={entree.id} entree={entree} chemin={chemin} />
        ))}
    </>
  );
}
